import * as Yup from 'yup';
import { Field, Form, Formik } from 'formik';
import Grid from '@material-ui/core/Grid';
import { Select, TextField } from 'formik-material-ui';
import React from 'react';
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';
import MenuItem from '@material-ui/core/MenuItem';
import { Button, CardActions, Divider } from '@material-ui/core';
import CardContent from '@material-ui/core/CardContent';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory } from 'react-router-dom';
import { cloneDeep, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { isRetainerPatient, replaceNullToEmpty, UTCDateTimeToLocalTime } from '../../../helpers';
import LoadingButton from '../../../components/LoadingButton';
import ConfirmationDialogOnClickWrapper from '../../../components/ConfirmationDialogOnClickWrapper';
import Can from '../../../components/Can';
import { PATIENT_STATUSES } from '../../../constants';
import PatientHoldingAction from './PatientHoldingAction';
import patterns from '../../../constants/patterns';
import FlawedTrayCorrectionButton from '../../../components/PatientRequest/FlawedTrayCorrectionButton';
import PatientArchiveAction from './PatientArchiveAction';
import PatientDeleteAction from './PatientDeleteAction';
import NameField from '../../../components/Formik/NameField';
import CustomTextField from '../../../components/Formik/TextField';
import ReverseCaseAction from './ReverseCaseAction';
import SanitizeForbiddenCharactersField from '../../../components/RxForm/Fields/Formik/SanitizeForbiddenCharactersField';

const useStyles = makeStyles(theme => ({
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(2)
  },
  submit: {
    marginLeft: 'auto'
  }
}));

const PatientForm = ({
  onSubmit,
  isLoading,
  patient,
  isDeleting,
  onDelete,
  isRestoring,
  onRestore,
  isMarkingAsReady,
  onMarkAsReady,
  isCompleting,
  isReversing,
  onComplete,
  onReverseCase,
  initialValues,
  isArchiving,
  onArchive
}) => {
  const classes = useStyles();
  const history = useHistory();
  const emptyPatient = {
    first_name: '',
    last_name: '',
    case_number_text: '',
    numeric_suffix: ''
  };

  const formatPatient = patientToFormat => {
    const formattedPatient = cloneDeep(patientToFormat);
    formattedPatient.created_at = UTCDateTimeToLocalTime(formattedPatient.created_at);
    formattedPatient.updated_at = UTCDateTimeToLocalTime(formattedPatient.updated_at);

    return formattedPatient;
  };

  return (
    <Formik
      initialValues={
        patient ? formatPatient(replaceNullToEmpty(patient)) : { ...emptyPatient, ...initialValues }
      }
      enableReinitialize
      validationSchema={Yup.object().shape({
        first_name: Yup.string()
          .required('Required')
          .matches(
            patterns.name,
            'Name may only contain letters (first letter uppercase), spaces, points, brackets, and apostrophes.'
          ),
        last_name: Yup.string()
          .required('Required')
          .matches(
            patterns.name,
            'Name may only contain letters (first letter uppercase), spaces, points, brackets, and apostrophes.'
          ),
        case_number_text: Yup.string()
          .nullable()
          .matches(
            patterns.case_number,
            'Case number may only contain letters, spaces, points, brackets, and apostrophes.'
          ),
        numeric_suffix: Yup.string().nullable()
      })}
      onSubmit={values => {
        return onSubmit(values);
      }}
    >
      {({ isValid, dirty }) => (
        <Form className={classes.form} noValidate>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Field
                  variant="outlined"
                  fullWidth
                  id="first_name"
                  label="First Name"
                  name="first_name"
                  component={SanitizeForbiddenCharactersField}
                  originalComponent={NameField}
                  disabled={patient && patient.deleted_at}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field
                  variant="outlined"
                  name="last_name"
                  fullWidth
                  id="last_name"
                  label="Last Name"
                  component={SanitizeForbiddenCharactersField}
                  originalComponent={NameField}
                  disabled={patient && patient.deleted_at}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field
                  variant="outlined"
                  name="case_number_text"
                  fullWidth
                  id="case_number_text"
                  label="Case Number"
                  removeUnnecessaryWhitespaces
                  component={SanitizeForbiddenCharactersField}
                  originalComponent={CustomTextField}
                  disabled={patient && patient.deleted_at}
                />
              </Grid>
              {patient && (
                <Grid item xs={12} sm={6}>
                  <Field
                    variant="outlined"
                    name="numeric_suffix"
                    fullWidth
                    id="numeric_suffix"
                    label="Suffix"
                    component={TextField}
                    disabled={patient && patient.deleted_at}
                  />
                </Grid>
              )}
              {patient && (
                <>
                  <Grid item xs={12} sm={6}>
                    <FormControl variant="outlined" fullWidth>
                      <InputLabel>Status</InputLabel>
                      <Field
                        required
                        name="patient_status_id"
                        disabled
                        component={Select}
                        labelWidth={45}
                      >
                        {Object.keys(PATIENT_STATUSES).map(key => {
                          return (
                            <MenuItem key={key} value={key}>
                              {PATIENT_STATUSES[key].name}
                            </MenuItem>
                          );
                        })}
                      </Field>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field
                      variant="outlined"
                      name="created_at"
                      fullWidth
                      disabled
                      label="Creation Date"
                      component={TextField}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field
                      variant="outlined"
                      name="updated_at"
                      fullWidth
                      disabled
                      label="Modification Date"
                      component={TextField}
                    />
                  </Grid>
                  {patient.deleted_at && (
                    <Grid item xs={12} sm={6}>
                      <Field
                        variant="outlined"
                        name="deleted_at"
                        fullWidth
                        disabled
                        label="Deletion Date"
                        component={TextField}
                      />
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          </CardContent>
          <Divider />
          <CardActions>
            {patient && !patient.deleted_at && patient.rx_form && (
              <>
                {!patient.rx_form.ready_at &&
                  !patient.rx_form.cancelled_at &&
                  Number(patient.rx_form.submission_completed_by) === 1 && (
                    <Can
                      permissions={['rx-forms.manage', 'rx-forms.mark-as-ready']}
                      yes={() => (
                        <ConfirmationDialogOnClickWrapper
                          confirmationBody="Are you sure you want to notify the doctor now?"
                          confirmationTitle="Notify Doctor"
                        >
                          <LoadingButton
                            variant="contained"
                            color="primary"
                            className={classes.submit}
                            disabled={isMarkingAsReady}
                            loading={isMarkingAsReady}
                            onClick={() => onMarkAsReady(patient.rx_form.id)}
                          >
                            Notify
                          </LoadingButton>
                        </ConfirmationDialogOnClickWrapper>
                      )}
                    />
                  )}
              </>
            )}
            <FlawedTrayCorrectionButton patient={patient} className={classes.submit} />
            {patient && !patient.deleted_at && (
              <Can
                permissions={['patients.manage', 'patients.complete-setup', 'patients.supervise']}
                yes={() => (
                  <>
                    {patient.patient_status_id === 1 && !isRetainerPatient(patient) && (
                      <ConfirmationDialogOnClickWrapper
                        confirmationBody="Are you sure you want to change the patient's status to completed?"
                        confirmationTitle="Complete Patient"
                      >
                        <LoadingButton
                          loading={isCompleting}
                          color="primary"
                          variant="contained"
                          className={classes.submit}
                          disabled={isLoading || isCompleting}
                          onClick={onComplete}
                        >
                          Complete
                        </LoadingButton>
                      </ConfirmationDialogOnClickWrapper>
                    )}
                    {process.env.REACT_APP_CASE_REVERSAL_ENABLED === 'yes' && (
                      <ReverseCaseAction
                        patient={patient}
                        isLoading={isLoading}
                        isReversing={isReversing}
                        onReverse={onReverseCase}
                      />
                    )}
                    {!isRetainerPatient(patient) && (
                      <Button
                        variant="contained"
                        color="primary"
                        className={classes.submit}
                        onClick={() => history.push(`/patient-details/${patient.id}/print`)}
                      >
                        Print
                      </Button>
                    )}
                  </>
                )}
              />
            )}
            <LoadingButton
              type="submit"
              variant="contained"
              color="primary"
              loading={isLoading}
              disabled={(!dirty && isEmpty(initialValues)) || !isValid || isLoading}
              className={classes.submit}
            >
              {patient ? 'Update' : 'Create'}
            </LoadingButton>
            {patient && (
              <>
                <PatientHoldingAction patient={patient} isLoading={isLoading} />
                <PatientDeleteAction
                  patient={patient}
                  isLoading={isLoading}
                  isDeleting={isDeleting}
                  onDelete={onDelete}
                />
                <PatientArchiveAction
                  patient={patient}
                  isLoading={isLoading}
                  isArchiving={isArchiving}
                  isDeleting={isDeleting}
                  onArchive={onArchive}
                />
                {patient.deleted_at && (
                  <Can
                    permissions={['patients.manage', 'patients.supervise', 'patients.delete']}
                    yes={() => (
                      <LoadingButton
                        loading={isRestoring}
                        color="primary"
                        variant="contained"
                        disabled={isLoading || isDeleting || isRestoring}
                        onClick={onRestore}
                      >
                        Restore
                      </LoadingButton>
                    )}
                  />
                )}
              </>
            )}
          </CardActions>
        </Form>
      )}
    </Formik>
  );
};

PatientForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  patient: PropTypes.object,
  onDelete: PropTypes.func,
  isDeleting: PropTypes.bool,
  onRestore: PropTypes.func,
  isRestoring: PropTypes.bool,
  isMarkingAsReady: PropTypes.bool,
  onMarkAsReady: PropTypes.func,
  isCompleting: PropTypes.bool,
  isReversing: PropTypes.bool,
  onComplete: PropTypes.func,
  onReverseCase: PropTypes.func,
  isArchiving: PropTypes.bool,
  onArchive: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  initialValues: PropTypes.object
};

PatientForm.defaultProps = {
  patient: null,
  onDelete: () => {},
  isDeleting: false,
  onRestore: () => {},
  isRestoring: false,
  isMarkingAsReady: false,
  onMarkAsReady: () => {},
  isCompleting: false,
  isReversing: false,
  onComplete: () => {},
  onReverseCase: () => {},
  isArchiving: false,
  onArchive: () => {},
  initialValues: {}
};

export default PatientForm;
